import { useState } from 'react'
import type { MouseEvent } from 'react'
import { Box, ButtonBase, Menu, MenuItem, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { colorTokens } from '../../../../shared/styles/theme'
import { RADIUS } from '../../../../shared/styles/radii'
import { ICON_SIZE } from '../../../../shared/styles/typography'

/**
 * 現在値だけを text pill で見せ、click で dropdown を開く選択プリミティブ
 * (02-components/pill-select.md)。選択は **✓ + 太字のみ** で示し、色で出さない。
 *
 * trigger: 32px / padding 0 10px / 999px / 13px 500 / fg.muted、open 中は fg.default + overlay.hover
 * menu:    min-width 220 / radius 12 / padding 4 / border.subtle / shadow.popover
 * item:    左 18px の ✓ スロット + ラベル 14px、hover overlay.hover
 */

interface Props {
  ariaLabel: string
  value: string | null
  choices: string[]
  onChange: (value: string) => void
  /** 未選択のときの表示。既定「未設定」 */
  placeholder?: string
}

export const PillSelect = ({
  ariaLabel,
  value,
  choices,
  onChange,
  placeholder = '未設定',
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const isOpen = Boolean(anchor)

  const open = (event: MouseEvent<HTMLElement>) =>
    setAnchor(event.currentTarget)
  const close = () => setAnchor(null)

  return (
    <>
      <ButtonBase
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={open}
        sx={{
          height: 32,
          px: 1.25,
          gap: 0.25,
          borderRadius: `${RADIUS.full}px`,
          fontSize: 13,
          fontWeight: 500,
          color: isOpen
            ? colorTokens.fg.default
            : value
            ? colorTokens.fg.muted
            : colorTokens.fg.faint,
          background: isOpen ? colorTokens.overlay.hover : 'transparent',
          transition: 'background-color 120ms, color 120ms',
          '&:hover': {
            background: colorTokens.overlay.hover,
            color: colorTokens.fg.default,
          },
          '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
        }}
      >
        {value ?? placeholder}
        <KeyboardArrowDownIcon sx={{ fontSize: ICON_SIZE.md }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={isOpen}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 220,
              p: 0.5,
              borderRadius: '12px',
              border: `1px solid ${colorTokens.border.subtle}`,
              boxShadow: colorTokens.shadow.popover,
            },
          },
          list: { sx: { p: 0 } },
        }}
      >
        {choices.map((choice) => {
          const selected = choice === value
          return (
            <MenuItem
              key={choice}
              selected={selected}
              onClick={() => {
                onChange(choice)
                close()
              }}
              sx={{
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: `${RADIUS.lg}px`,
                '&:hover': { background: colorTokens.overlay.hover },
                '&.Mui-selected, &.Mui-selected:hover': {
                  background: colorTokens.overlay.selected,
                },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  display: 'flex',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {selected && (
                  <CheckIcon
                    sx={{
                      fontSize: ICON_SIZE.md,
                      color: colorTokens.fg.default,
                    }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: selected ? 600 : 500,
                  color: colorTokens.fg.default,
                  lineHeight: 1.4,
                }}
              >
                {choice}
              </Typography>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
